import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

export interface DiagramTab {
  id: string;
  name: string;
  color?: string | null;
  icon?: string | null;
  iconColor?: string | null;
}

interface TabsContextValue {
  tabs: DiagramTab[];
  openTab: (tab: DiagramTab) => void;
  closeTab: (id: string) => void;
  // Settings behaves like an ordinary closable tab (only Home is permanent).
  settingsOpen: boolean;
  openSettingsTab: () => void;
  closeSettingsTab: () => void;
}

const TabsContext = createContext<TabsContextValue | undefined>(undefined);
const STORAGE_KEY = 'ss:open-tabs';
const SETTINGS_KEY = 'ss:settings-tab';

export function TabsProvider({ children }: { children: React.ReactNode }) {
  const [tabs, setTabs] = useState<DiagramTab[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as DiagramTab[]) : [];
    } catch {
      return [];
    }
  });
  const [settingsOpen, setSettingsOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SETTINGS_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tabs));
  }, [tabs]);

  useEffect(() => {
    localStorage.setItem(SETTINGS_KEY, settingsOpen ? '1' : '0');
  }, [settingsOpen]);

  const openSettingsTab = useCallback(() => setSettingsOpen(true), []);
  const closeSettingsTab = useCallback(() => setSettingsOpen(false), []);

  const openTab = useCallback((tab: DiagramTab) => {
    setTabs((prev) =>
      prev.some((t) => t.id === tab.id)
        ? prev.map((t) => (t.id === tab.id ? { ...t, ...tab } : t))
        : [...prev, tab]
    );
  }, []);

  const closeTab = useCallback((id: string) => {
    setTabs((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <TabsContext.Provider value={{ tabs, openTab, closeTab, settingsOpen, openSettingsTab, closeSettingsTab }}>
      {children}
    </TabsContext.Provider>
  );
}

export function useTabs() {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error('useTabs must be used within a TabsProvider');
  return ctx;
}
